import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { extractPhoneNumberDigits } from "@src/appV2/lib";
import {
  APP_V2_APP_EVENTS,
  APP_V2_USER_EVENTS,
  logError,
  logEvent,
} from "@src/appV2/lib/analytics";
// eslint-disable-next-line no-restricted-imports
import { getCognitoSingleton } from "@src/lib/cognito/src/lib/cognitoSingleton";
import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { z } from "zod";

import { type ApiResponse, post } from "../../api/api";
import { type AuthOtpErrorCode } from "../LoginSignUp/types";
import { IdentifyProviderEnum, identifyProviderSchema } from "./types";

const otpChannelSchema = z.enum(["sms", "call", "whatsapp"]);

const otpRequestSchema = z.object({
  isSignup: z.boolean(),
  phone: z.string(),
  channel: otpChannelSchema.optional(),
});

const backendMainotpSuccessResponseSchema = z.object({
  data: z.string().optional(),
  message: z.string(),
  /**
   * error is a string that is interspersed in the server code
   * See https://github.com/ClipboardHealth/clipboard-health/blob/development/projects/backend-main/src/routes/agentProfile/controller/requestOTP.ts#L42
   * TODO - should we use `z.enum` here? If so, we will need to increase
   * confidence about syncing all error codes with the API code.
   */
  error: z.string().optional(),
});

const otpSuccessResponseSchema = backendMainotpSuccessResponseSchema.extend({
  identityProvider: identifyProviderSchema,
  isSignup: z.boolean().optional(),
});

export type OtpRequestChannel = z.infer<typeof otpChannelSchema>;
export type OtpRequest = z.infer<typeof otpRequestSchema>;
export type RequestOtpSuccessResponse = z.infer<typeof otpSuccessResponseSchema>;
export interface RequestOtpErrorResponse {
  message: string;
  error: AuthOtpErrorCode;
}

export const REQUEST_OTP_PATH = "/agentProfile/requestOTP";

export function useRequestOtp(
  options: UseMutationOptions<
    ApiResponse<RequestOtpSuccessResponse>,
    AxiosError<RequestOtpErrorResponse>,
    OtpRequest
  > = {}
): UseMutationResult<
  ApiResponse<RequestOtpSuccessResponse>,
  AxiosError<RequestOtpErrorResponse>,
  OtpRequest
> {
  const isCognitoOnboardingEnabled = useCbhFlag(CbhFeatureFlag.ENABLE_COGNITO_ONBOARDING, {
    defaultValue: false,
  });
  const isCognitoPhoneLoginEnabled = useCbhFlag(CbhFeatureFlag.ENABLE_COGNITO_PHONE_LOGIN, {
    defaultValue: false,
  });

  return useMutation<
    ApiResponse<RequestOtpSuccessResponse>,
    AxiosError<RequestOtpErrorResponse>,
    OtpRequest
  >({
    mutationFn: async ({ isSignup, phone, channel }) => {
      const phoneNumber = extractPhoneNumberDigits(phone);
      const phoneNumberInE164 = `+1${phoneNumber}`;

      // Cognito for signup (onboarding)
      if (isCognitoOnboardingEnabled && isSignup) {
        const cognitoAuth = getCognitoSingleton();

        try {
          await cognitoAuth.signUp(phoneNumberInE164);
          logEvent(APP_V2_USER_EVENTS.ONBOARDING_IDP_SELECTED, {
            identityProvider: IdentifyProviderEnum.cognito,
            isSignup,
          });
          return {
            status: 200,
            statusText: "OK",
            data: {
              message: "otpIsValid",
              identityProvider: IdentifyProviderEnum.cognito,
            },
          } satisfies ApiResponse<RequestOtpSuccessResponse>;
        } catch (error) {
          logError(APP_V2_APP_EVENTS.REQUEST_OTP_FAILURE, {
            error,
            metadata: {
              error,
              identityProvider: IdentifyProviderEnum.cognito,
              phoneNumber,
              fallbackToFirebase: true,
            },
          });
        }
      }

      // Cognito for login (phone OTP)
      // Always try signUp first to force backend synchronization, then fall back to signIn.
      if (isCognitoPhoneLoginEnabled && !isSignup) {
        const cognitoAuth = getCognitoSingleton();

        // Step 1: Try signUp to force backend sync (creates Cognito user if not exists)
        try {
          await cognitoAuth.signUp(phoneNumberInE164);
          logEvent(APP_V2_USER_EVENTS.ONBOARDING_IDP_SELECTED, {
            identityProvider: IdentifyProviderEnum.cognito,
            isSignup: true,
          });
          return {
            status: 200,
            statusText: "OK",
            data: {
              message: "otpIsValid",
              identityProvider: IdentifyProviderEnum.cognito,
              isSignup: true,
            },
          } satisfies ApiResponse<RequestOtpSuccessResponse>;
        } catch (signUpError) {
          // signUp failed (user already exists), proceed to signIn
          logError(APP_V2_APP_EVENTS.REQUEST_OTP_FAILURE, {
            error: signUpError,
            metadata: {
              identityProvider: IdentifyProviderEnum.cognito,
              phoneNumber,
              fallbackToSignIn: true,
            },
          });
        }

        // Step 2: Fall back to signIn (requestPhoneOtp)
        try {
          await cognitoAuth.requestPhoneOtp(phoneNumberInE164);
          logEvent(APP_V2_USER_EVENTS.ONBOARDING_IDP_SELECTED, {
            identityProvider: IdentifyProviderEnum.cognito,
            isSignup: false,
          });
          return {
            status: 200,
            statusText: "OK",
            data: {
              message: "otpIsValid",
              identityProvider: IdentifyProviderEnum.cognito,
              isSignup: false,
            },
          } satisfies ApiResponse<RequestOtpSuccessResponse>;
        } catch (error) {
          const cognitoError = error as { code?: string };
          const recoverableCodes = ["auth/user-not-found", "auth/sms-otp-not-available"];
          if (!recoverableCodes.includes(cognitoError.code ?? "")) {
            throw error;
          }
          logError(APP_V2_APP_EVENTS.REQUEST_OTP_FAILURE, {
            error,
            metadata: {
              identityProvider: IdentifyProviderEnum.cognito,
              phoneNumber,
              fallbackToFirebase: true,
              reason: cognitoError.code,
            },
          });
        }
      }

      const response = await post({
        url: REQUEST_OTP_PATH,
        data: { isSignup, phone: phoneNumber, channel },
        requestSchema: otpRequestSchema,
        responseSchema: backendMainotpSuccessResponseSchema,
      });

      if (isSignup) {
        logEvent(APP_V2_USER_EVENTS.ONBOARDING_IDP_SELECTED, {
          identityProvider: IdentifyProviderEnum.firebase,
          isSignup,
        });
      }

      return {
        ...response,
        data: otpSuccessResponseSchema.parse({
          ...response.data,
          identityProvider: IdentifyProviderEnum.firebase,
        }),
      };
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.REQUEST_OTP_FAILURE,
    },
    ...options,
  });
}
